import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sun, UtensilsCrossed, Factory, Nut, Apple, Sparkles, GraduationCap, Home as HomeIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { preserveEleone } from "@/utils/preserveEleone";

export default function Sectors() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;

 const SECTORS = [
 {
 icon: Sun,
 to: "/secteurs/energie",
 house: "NANDA SÁ ENERGIA PRO",
 title: l("Énergies renouvelables", "Renewable Energy", "Energias renováveis"),
 body: l(
 "Solutions solaires professionnelles pour entreprises et collectivités : études, installation, maintenance et stockage d'énergie, en Guinée-Bissau.",
 "Professional solar solutions for companies and communities: studies, installation, maintenance and energy storage, from Guinea-Bissau to Guinea-Bissau.",
 "Soluções solares profissionais para empresas e colectividades: estudos, instalação, manutenção e armazenamento de energia, da Guiné-Bissau ao Guinea-Bissau."
 ),
 items: [
 l("Systèmes solaires", "Solar systems", "Sistemas solares"),
 l("Stockage d'énergie", "Energy storage", "Armazenamento de energia"),
 l("Installations professionnelles", "Professional installations", "Instalações profissionais"),
 l("Services techniques", "Technical services", "Serviços técnicos"),
 ],
 },
 {
 icon: UtensilsCrossed,
 to: "/marques/nanda-sa",
 house: "NANDA SÁ",
 title: l("Épicerie fine & Gastronomie", "Fine Foods & Gourmet Products", "Mercearia fina & Gastronomia"),
 body: l(
 "Une sélection exigeante de produits gastronomiques ouest-africains : recettes traditionnelles, spécialités locales, boissons naturelles et conserves premium.",
 "A demanding selection of West African gastronomic products: traditional recipes, local specialties, natural beverages and premium preserves.",
 "Uma selecção exigente de produtos gastronómicos oeste-africanos: receitas tradicionais, especialidades locais, bebidas naturais e conservas premium."
 ),
 items: [
 l("Épicerie premium", "Premium grocery", "Mercearia premium"),
 l("Spécialités locales", "Local specialties", "Especialidades locais"),
 l("Recettes traditionnelles", "Traditional recipes", "Receitas tradicionais"),
 l("Conserves & boissons", "Preserves & beverages", "Conservas & bebidas"),
 ],
 },
 {
 icon: Factory,
 to: "/secteurs/agriculture",
 house: "NANDA SÁ",
 title: l("Transformation alimentaire", "Food Processing", "Transformação alimentar"),
 body: l(
 "Valeur ajoutée locale : nous transformons les matières premières ouest-africaines en produits finis premium, prêts pour les marchés locaux et l'export international.",
 "Local value-added: we transform West African raw materials into premium finished products, ready for local markets and international export.",
 "Valor acrescentado local: transformamos as matérias-primas oeste-africanas em produtos acabados premium, prontos para os mercados locais e a exportação internacional."
 ),
 items: [
 l("Miel de Mangrove", "Mangrove Honey", "Mel de Mangue"),
 l("Sel naturel d'Adéane", "Adéane natural salt", "Sal natural de Adéane"),
 l("Épices & condiments", "Spices & condiments", "Especiarias & condimentos"),
 l("Huiles vierges", "Virgin oils", "Óleos virgens"),
 ],
 },
 {
 icon: Nut,
 to: "/secteurs/cajou",
 house: "NANDA SÁ",
 title: l("Anacarde (Cashew)", "Cashew", "Caju"),
 body: l(
 "La Guinée-Bissau est l'un des premiers producteurs mondiaux d'anacarde. NANDA SÁ développe la filière avec des partenaires durables et une transformation à forte valeur ajoutée locale, de la noix brute à l'amande grillée.",
 "Guinea-Bissau is one of the world's leading cashew producers. NANDA SÁ develops the sector with sustainable partners and high value-added local processing, from raw nut to roasted kernel.",
 "A Guiné-Bissau é um dos principais produtores mundiais de caju. A NANDA SÁ desenvolve a fileira com parceiros duradouros e uma transformação de elevado valor acrescentado local, da castanha em bruto à amêndoa torrada."
 ),
 items: [
 l("Noix de cajou brutes", "Raw cashew nuts", "Castanha de caju em bruto"),
 l("Amandes transformées", "Processed kernels", "Amêndoas transformadas"),
 l("Packaging premium & export", "Premium packaging & export", "Embalagem premium & exportação"),
 l("Sourcing international", "International sourcing", "Sourcing internacional"),
 ],
 },
 {
 icon: Apple,
 to: "/secteurs/agriculture",
 house: "NANDA SÁ",
 title: l("Fruits tropicaux", "Tropical Fruits & Fruit Products", "Frutos tropicais"),
 body: l(
 "Mangues, ananas, hibiscus, baobab et bien d'autres. NANDA SÁ valorise les fruits tropicaux d'Afrique de l'Ouest en produits séchés, en pulpes, en infusions et en huiles vierges.",
 "Mangoes, pineapples, hibiscus, baobab and many more. NANDA SÁ celebrates West African tropical fruits through dried products, pulps, infusions and virgin oils.",
 "Mangas, ananás, hibisco, baobá e muitos outros. A NANDA SÁ valoriza os frutos tropicais oeste-africanos em produtos secos, polpas, infusões e óleos virgens."
 ),
 items: [
 l("Fruits séchés bio", "Organic dried fruits", "Frutos secos bio"),
 l("Pulpes & poudres", "Pulps & powders", "Polpas & pós"),
 l("Infusions & tisanes", "Infusions & herbal teas", "Infusões & tisanas"),
 l("Huiles vierges", "Virgin oils", "Óleos virgens"),
 ],
 },
 {
 icon: Sparkles,
 to: "/cosmetiques",
 house: "NANDA SÁ Cosmétiques",
 title: l("Cosmétiques naturels", "Natural Cosmetics", "Cosméticos naturais"),
 body: l(
 "NANDA SÁ Cosmétiques élève les plantes d'Afrique de l'Ouest en soins de peau, huiles végétales, beurre de karité et rituels de bien-être, pour un rapport à la beauté juste, sensible et durable.",
 "NANDA SÁ Cosmétiques elevates West African plants into skincare, plant oils, shea butter and wellness rituals, for a beauty relationship that is true, sensitive and lasting.",
 "A NANDA SÁ Cosmétiques eleva as plantas oeste-africanas em cuidados de pele, óleos vegetais, manteiga de karité e rituais de bem-estar, para uma relação com a beleza justa, sensível e duradoura."
 ),
 items: [
 l("Soins de peau", "Skincare", "Cuidados de pele"),
 l("Huiles végétales", "Plant oils", "Óleos vegetais"),
 l("Beurre de karité", "Shea butter", "Manteiga de karité"),
 l("Soins corps & cheveux", "Body & hair care", "Cuidados corpo & cabelo"),
 ],
 },
 {
 icon: GraduationCap,
 to: "/marques/academie",
 house: "NANDA SÁ Académie",
 title: l("Éducation & Formation", "Education", "Educação"),
 body: l(
 "L'Académie forme la génération suivante : leadership, entrepreneuriat, compétences numériques, énergies renouvelables et développement personnel, pour la jeunesse ouest-africaine.",
 "The Academy trains the next generation: leadership, entrepreneurship, digital skills, renewable energy and personal development, for West African youth.",
 "A Academia forma a próxima geração: liderança, empreendedorismo, competências digitais, energias renováveis e desenvolvimento pessoal, para a juventude oeste-africana."
 ),
 items: [
 l("Formation professionnelle", "Professional training", "Formação profissional"),
 l("Leadership & Entrepreneuriat", "Leadership & Entrepreneurship", "Liderança & Empreendedorismo"),
 l("Compétences numériques", "Digital skills", "Competências digitais"),
 l("Développement de la jeunesse", "Youth development", "Desenvolvimento da juventude"),
 ],
 },
 {
 icon: HomeIcon,
 to: "/marques/john-tado",
 house: "JOHN TĀDO",
 title: l("Art de vivre africain", "African Lifestyle", "Arte de viver africana"),
 body: l(
 "JOHN TĀDO est la Maison d'art de vivre du Groupe : design, décoration, mobilier, arts de la table, mode, livres et gastronomie, une expérience culturelle plutôt qu'un e-commerce.",
 "JOHN TĀDO is the Group's lifestyle House: design, decoration, furniture, tableware, fashion, books and gastronomy, a cultural experience rather than an e-commerce.",
 "A JOHN TĀDO é a Casa de arte de viver do Grupo: design, decoração, mobiliário, artes de mesa, moda, livros e gastronomia, uma experiência cultural em vez de um e-commerce."
 ),
 items: [
 l("Design africain contemporain", "Contemporary African design", "Design africano contemporâneo"),
 l("Décoration & mobilier", "Decoration & furniture", "Decoração & mobiliário"),
 l("Gastronomie & culture", "Gastronomy & culture", "Gastronomia & cultura"),
 l("Mode & artisanat", "Fashion & craftsmanship", "Moda & artesanato"),
 ],
 },
 ];

 return (
 <div data-testid="page-sectors">
 {/* Hero */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">{l("Nos Secteurs", "Our Sectors", "Os nossos Sectores")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">
 {l("Une entreprise ouest-africaine, plurielle par nature.", "A West African company, plural by nature.", "Uma empresa oeste-africana, plural por natureza.")}
 </h1>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l(
 "NANDA SÁ développe des produits premium et des solutions durables enracinés en Afrique de l'Ouest, pour les marchés locaux et internationaux. Découvrez les huit secteurs qui composent notre Groupe.",
 "NANDA SÁ develops premium products and sustainable solutions rooted in West Africa, for local and international markets. Discover the eight sectors that shape our Group.",
 "A NANDA SÁ desenvolve produtos premium e soluções sustentáveis enraizados na África Ocidental, para os mercados locais e internacionais. Descubra os oito sectores que compõem o nosso Grupo."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Sectors editorial cards */}
 <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {SECTORS.map((s, i) => {
 const Icon = s.icon;
 return (
 <StaggerItem key={i} className="bg-nanda-bg" data-testid={`sector-${i}`}>
 <Link
 to={s.to}
 className="group block h-full p-10 md:p-14 hover:bg-nanda-bgAlt transition-colors duration-700"
 data-testid={`sector-link-${i}`}
 >
 <div className="flex items-start justify-between mb-10">
 <div className="text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500">
 <Icon size={34} strokeWidth={1.2} />
 </div>
 <div className="label-overline text-nanda-muted">{String(i + 1).padStart(2, "0")}</div>
 </div>
 <div className="label-overline text-nanda-terracotta mb-4">{preserveEleone(s.house)}</div>
 <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-nanda-ink mb-6">{s.title}</h2>
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed max-w-lg">{s.body}</p>
 <ul className="mt-8 space-y-2.5">
 {s.items.map((it, j) => (
 <li key={j} className="flex items-center gap-3 text-nanda-ink text-sm">
 <span className="h-px w-5 bg-nanda-ink/60" />
 {it}
 </li>
 ))}
 </ul>
 <span className="mt-10 inline-flex items-center gap-3 label-overline text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">
 {l("Découvrir la Maison", "Discover the House", "Descobrir a Casa")}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </span>
 </Link>
 </StaggerItem>
 );
 })}
 </Stagger>
 </section>

 {/* Filières d'investissement. Panorama complet */}
 <section className="border-t border-nanda-line" data-testid="sectors-filieres">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-5">{l("Filières d'investissement", "Investment value chains", "Fileiras de investimento")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {l(
 "Treize filières, un seul territoire d'action.",
 "Thirteen value chains, a single territory of action.",
 "Treze fileiras, um único território de acção."
 )}
 </h2>
 <p className="mt-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed max-w-2xl">
 {l(
 "Chaque filière dispose d'une page dédiée : chiffres clés, segments d'investissement, défis, passerelles NANDA SÁ et note PDF téléchargeable.",
 "Each value chain has a dedicated page: key figures, investment segments, challenges, NANDA SÁ pathways and downloadable PDF brief.",
 "Cada fileira tem uma página dedicada: dados-chave, segmentos de investimento, desafios, pontes NANDA SÁ e nota PDF descarregável."
 )}
 </p>
 </FadeUp>
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {[
 { to: "/secteurs/cajou", label: l("Cajou", "Cashew", "Caju") },
 { to: "/secteurs/agriculture", label: l("Agriculture", "Agriculture", "Agricultura") },
 { to: "/secteurs/elevage", label: l("Élevage", "Livestock", "Pecuária") },
 { to: "/secteurs/peche", label: l("Pêche", "Fishing", "Pesca") },
 { to: "/secteurs/energie", label: l("Énergie solaire", "Solar energy", "Energia solar") },
 { to: "/secteurs/immobilier", label: l("Immobilier", "Real estate", "Imobiliário") },
 { to: "/secteurs/tourisme", label: l("Tourisme", "Tourism", "Turismo") },
 { to: "/secteurs/artisanat", label: l("Artisanat", "Crafts", "Artesanato") },
 { to: "/secteurs/commerce", label: l("Commerce", "Commerce", "Comércio") },
 { to: "/secteurs/education", label: l("Éducation", "Education", "Educação") },
 { to: "/secteurs/numerique", label: l("Numérique", "Digital", "Digital") },
 { to: "/secteurs/logistique", label: l("Logistique", "Logistics", "Logística") },
 { to: "/secteurs/industrie", label: l("Industrie", "Industry", "Indústria") },
 ].map((f) => (
 <Link
 key={f.to}
 to={f.to}
 data-testid={`filiere-${f.to.split("/").pop}`}
 className="group bg-nanda-bg p-8 md:p-10 hover:bg-nanda-sand/40 transition-colors duration-500 flex items-center justify-between gap-4"
 >
 <div className="font-serif text-lg md:text-xl text-nanda-ink leading-tight">{f.label}</div>
 <ArrowUpRight size={18} strokeWidth={1.3} className="text-nanda-terracotta shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
 </Link>
 ))}
 </div>
 </div>
 </section>
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
 <FadeUp className="md:col-span-8">
 <div className="label-overline text-nanda-muted mb-5">{l("Bâtir ensemble", "Building together", "Construir juntos")}</div>
 <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-balance">
 {l("Vous êtes distributeur, producteur, investisseur ou partenaire ?", "Are you a distributor, producer, investor or partner?", "É distribuidor, produtor, investidor ou parceiro?")}
 </h2>
 <p className="mt-6 max-w-xl text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {l(
 "Nos équipes reçoivent chaque année des demandes en provenance d'Afrique, d'Europe et d'Amérique du Nord. Nous étudions chaque opportunité avec exigence.",
 "Our teams receive requests every year from Africa, Europe and North America. We study every opportunity with rigour.",
 "As nossas equipas recebem todos os anos pedidos vindos de África, da Europa e da América do Norte. Estudamos cada oportunidade com exigência."
 )}
 </p>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-4 md:text-right">
 <Link
 to="/contact"
 data-testid="sectors-cta-contact"
 className="inline-flex items-center gap-3 bg-nanda-terracotta text-white px-8 py-4 hover:bg-[#8F5B3F] transition-colors duration-500 label-overline"
 >
 {l("Nous écrire", "Contact us", "Escreva-nos")}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 </div>
 );
}
